import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useTrackedLinks, trackedIntent, trackedCopyLink, trackedEmailHref } from "@/lib/utm";
import { Button } from "@/components/ui/button";
import SermonEditor from "@/components/SermonEditor";
import { ScrollText, Sparkles, Lock, Loader2, Share2, Twitter, Facebook, MessageCircle, Link2, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import ShareImagePicker from "@/components/ShareImagePicker";

// Study → Sermon (paid, feature-flagged). Shared by desktop rail + mobile tools.
export default function SermonPanel({ session }) {
  const navigate = useNavigate();
  const [feat, setFeat] = useState(null);
  const [sermon, setSermon] = useState(session.sermon || "");
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null); // {read_url, hook}
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    api.get("/me/subscription")
      .then(({ data }) => setFeat(data.features?.study_to_sermon || null))
      .catch(() => {});
  }, []);

  const create = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/study/sessions/${session.id}/sermon`);
      setSermon(data.sermon);
      setShare(null);
      toast.success(`Sermon outline ready · ${data.cost} credits used`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const startShare = async () => {
    if (share) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/study/sessions/${session.id}/sermon-share`);
      setShare(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSharing(false);
  };

  const tracked = useTrackedLinks(share?.read_url || "", "sermon_share");
  const socialUrl = (net) => trackedIntent(net, tracked, share?.read_url, share?.hook || "A sermon outline from OpenWord AI", "sermon_share");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackedCopyLink(tracked, share?.read_url || "", "sermon_share"));
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  const saveEdits = async (text) => {
    setSavingEdit(true);
    try {
      await api.post(`/study/sessions/${session.id}/sermon/save`, { sermon: text });
      toast.success("Edits saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSavingEdit(false);
      throw e;  // keep the editor open so the user can retry
    }
    setSavingEdit(false);
  };

  if (!feat?.enabled) return null;

  return (
    <section data-testid="tool-sermon" className="px-6 py-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <ScrollText className="w-4 h-4" strokeWidth={1.5} /> Sermon outline
        </span>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">
        Turn this study into a shareable, preachable outline — a clear big idea and the movements the
        passage itself calls for, with application and a closing. Edit it before you export.
      </p>

      {!feat.available ? (
        <button
          data-testid="sermon-upgrade"
          onClick={() => navigate("/billing")}
          className="w-full flex items-center justify-center gap-2 rounded-full border border-[#00E5FF]/40 py-2.5 text-sm text-[#00E5FF] hover:bg-[#10222A] transition-colors"
        >
          <Lock className="w-4 h-4" strokeWidth={1.5} /> Available on Pro &amp; above — see plans
        </button>
      ) : (
        <Button
          data-testid="sermon-generate"
          onClick={create}
          disabled={loading}
          className="w-full rounded-full text-white"
          style={{ backgroundColor: "#00E5FF" }}
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Composing&hellip;</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {sermon ? "Regenerate" : "Create sermon outline"} · {feat.cost} credits</>
          )}
        </Button>
      )}

      {sermon && (
        <>
          <SermonEditor
            sermon={sermon}
            onSermonChange={setSermon}
            title={session.study?.title || "Sermon outline"}
            onSave={saveEdits}
            saving={savingEdit}
            testid="sermon"
          />

          {/* Share funnel */}
          {!share ? (
            <Button
              data-testid="sermon-share-start"
              onClick={startShare}
              disabled={sharing}
              variant="outline"
              className="w-full rounded-full border-[#00E5FF]/40 text-[#00E5FF]"
            >
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Share</>}
            </Button>
          ) : (
            <div className="rounded-xl bg-[#0E131F] border border-[#1E273D] p-4 space-y-3" data-testid="sermon-share-panel">
              <p className="text-xs text-slate-500">Share a hook that invites people to read it on OpenWord AI.</p>
              <p className="text-sm text-slate-400 italic leading-relaxed">"{share.hook}"</p>
              <ShareImagePicker token={share.token} imageUrl={share.image_url} initialCustom={share.custom_image} testid="sermon-panel-share-img" />
              <div className="flex items-center gap-2">
                <a data-testid="share-x" aria-label="Share on X" title="Share on X" href={socialUrl("x")} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A]"><Twitter className="w-4 h-4" strokeWidth={1.5} /></a>
                <a data-testid="share-fb" aria-label="Share on Facebook" title="Share on Facebook" href={socialUrl("fb")} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A]"><Facebook className="w-4 h-4" strokeWidth={1.5} /></a>
                <a data-testid="share-wa" aria-label="Share on WhatsApp" title="Share on WhatsApp" href={socialUrl("wa")} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A]"><MessageCircle className="w-4 h-4" strokeWidth={1.5} /></a>
                <a data-testid="share-email" aria-label="Share by email" title="Share by email" href={trackedEmailHref(tracked, share?.read_url, share?.hook || "A sermon outline from OpenWord AI", "A sermon outline from OpenWord AI", "sermon_share")} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A]"><Mail className="w-4 h-4" strokeWidth={1.5} /></a>
                <button data-testid="share-copy" aria-label="Copy share link" title="Copy share link" onClick={copyLink} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A]">{copied ? <Check className="w-4 h-4" strokeWidth={2} /> : <Link2 className="w-4 h-4" strokeWidth={1.5} />}</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
